import React, { useState } from 'react';
import { Check, Crown, FileText, Calendar, Percent, BookOpen, Sparkles, Pause, RefreshCw, ArrowRight, AlertTriangle, X, Clock, TrendingUp, Shield } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';
import { ZodiacTheme } from './zodiacThemes';
import { useLanguage } from '../../i18n/LanguageContext';
interface SubscriptionsTabProps {
  theme: ZodiacTheme;
  onNavigate: (page: string) => void;
}
export function SubscriptionsTab({
  theme,
  onNavigate
}: SubscriptionsTabProps) {
  const {
    t
  } = useLanguage();
  const {
    showToast
  } = useToast();
  const [hasSubscription, setHasSubscription] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  // Mock subscription data
  const sub = {
    plan: 'Astriqo Plus',
    price: 990,
    nextBilling: '12 Ноя 2024',
    daysLeft: 12,
    startDate: '12 Окт 2024',
    reportsUsed: 2,
    reportsTotal: 3,
    reportsRemaining: 1,
    savingsAmount: 2400,
    consultationsDiscount: 20,
    discountUsedTimes: 3,
    discountSaved: 1500
  };
  const benefits = [{
    icon: <FileText className="w-4 h-4" data-id="element-19517" />,
    label: t('userdash.subscriptions.benefitReports'),
    desc: t('userdash.subscriptions.benefitReportsDesc'),
    bg: 'bg-indigo-50',
    text: 'text-indigo-600'
  }, {
    icon: <Percent className="w-4 h-4" data-id="element-19518" />,
    label: t('userdash.subscriptions.benefitDiscount'),
    desc: t('userdash.subscriptions.benefitDiscountDesc'),
    bg: 'bg-emerald-50',
    text: 'text-emerald-600'
  }, {
    icon: <BookOpen className="w-4 h-4" data-id="element-19519" />,
    label: t('userdash.subscriptions.benefitArticles'),
    desc: t('userdash.subscriptions.benefitArticlesDesc'),
    bg: 'bg-amber-50',
    text: 'text-amber-600'
  }, {
    icon: <Calendar className="w-4 h-4" data-id="element-19520" />,
    label: t('userdash.subscriptions.benefitCalendar'),
    desc: t('userdash.subscriptions.benefitCalendarDesc'),
    bg: 'bg-purple-50',
    text: 'text-purple-600'
  }];
  const plans = [{
    name: t('userdash.subscriptions.planFree'),
    price: 0,
    features: [t('userdash.subscriptions.featureBasicCalendar'), t('userdash.subscriptions.featureGeneralForecasts'), t('userdash.subscriptions.feature1Profile')],
    current: false
  }, {
    name: 'Astriqo Plus',
    price: 990,
    features: [t('userdash.subscriptions.feature3Reports'), t('userdash.subscriptions.feature20Discount'), t('userdash.subscriptions.featureProCalendar'), t('userdash.subscriptions.featureProArticles'), t('userdash.subscriptions.feature5Profiles')],
    current: false,
    popular: true
  }, {
    name: 'Astriqo Pro',
    price: 1990,
    features: [t('userdash.subscriptions.feature10Reports'), t('userdash.subscriptions.feature30Discount'), t('userdash.subscriptions.featurePrioritySupport'), t('userdash.subscriptions.featureAllPro'), t('userdash.subscriptions.featureUnlimitedProfiles')],
    current: false
  }];
  // No subscription state
  if (!hasSubscription) {
    return <div className="space-y-5" data-id="element-19521">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-19522">
          <div data-id="element-19523">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-19524">
              {t('userdash.subscriptions.title')}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5" data-id="element-19525">
              {t('userdash.subscriptions.choosePlan')}
            </p>
          </div>
        </div>

        {/* Social proof */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 flex items-center gap-3" data-id="element-19526">
          <TrendingUp className="w-5 h-5 text-indigo-600 shrink-0" data-id="element-19527" />
          <p className="text-xs text-indigo-800" data-id="element-19528">
            <span className="font-bold" data-id="element-19529">
              {t('userdash.subscriptions.socialProofBold')}
            </span>{' '}
            {t('userdash.subscriptions.socialProofText')}
          </p>
        </div>

        {/* Plan comparison */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-19530">
          {plans.map(plan => <div key={plan.name} className={`bg-white border rounded-xl p-4 relative ${plan.popular ? 'border-indigo-300 ring-1 ring-indigo-200' : 'border-slate-200'}`} data-id="element-19531">
              {plan.popular && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-[9px] font-bold px-2.5 py-0.5 rounded-full" data-id="element-19532">
                  {t('userdash.subscriptions.popular')}
                </span>}
              <h4 className="font-bold text-slate-900 text-sm mb-1" data-id="element-19533">
                {plan.name}
              </h4>
              <div className="mb-3" data-id="element-19534">
                {plan.price > 0 ? <span className="text-2xl font-bold text-slate-900" data-id="element-19535">
                    {plan.price} ₽
                    <span className="text-xs font-normal text-slate-500" data-id="element-19536">
                      /{t('userdash.subscriptions.month')}
                    </span>
                  </span> : <span className="text-2xl font-bold text-slate-900" data-id="element-19537">0 ₽</span>}
              </div>
              <ul className="space-y-1.5 mb-4" data-id="element-19538">
                {plan.features.map((f, i) => <li key={i} className="flex items-center gap-1.5 text-xs text-slate-600" data-id="element-19539">
                    <Check className="w-3 h-3 text-emerald-500 shrink-0" data-id="element-19540" /> {f}
                  </li>)}
              </ul>
              <Button variant={plan.popular ? 'primary' : 'outline'} size="sm" className="w-full text-xs" onClick={() => {
            setHasSubscription(true);
            showToast(t('userdash.subscriptions.planActivated', {
              name: plan.name
            }));
          }} data-id="element-19541">
                {plan.price === 0 ? t('userdash.subscriptions.currentPlan') : t('userdash.subscriptions.subscribe')}
              </Button>
            </div>)}
        </div>
      </div>;
  }
  return <div className="space-y-5" data-id="element-19542">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-19543">
        <div data-id="element-19544">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-19545">
            {t('userdash.subscriptions.mySubscription')}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5" data-id="element-19546">
            {t('userdash.subscriptions.manageStats')}
          </p>
        </div>
      </div>

      {/* Status Card */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm relative overflow-hidden" data-id="element-19547">
        <div className="absolute top-0 right-0 opacity-5" data-id="element-19548">
          <Crown className="w-28 h-28 -mt-4 -mr-4" data-id="element-19549" />
        </div>
        <div className="relative z-10" data-id="element-19550">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4" data-id="element-19551">
            <div data-id="element-19552">
              <div className="flex items-center gap-2 mb-1" data-id="element-19553">
                <h3 className="text-xl font-bold text-slate-900" data-id="element-19554">{sub.plan}</h3>
                {isPaused ? <Badge variant="warning" size="sm" className="text-[10px]" data-id="element-19555">
                    {t('userdash.subscriptions.paused')}
                  </Badge> : <Badge variant="success" size="sm" className="text-[10px]" data-id="element-19556">
                    {t('userdash.subscriptions.active')}
                  </Badge>}
              </div>
              <p className="text-slate-500 text-xs" data-id="element-19557">
                {t('userdash.subscriptions.monthlySubscription')}
              </p>
            </div>
            <div className="text-right" data-id="element-19558">
              <div className="text-3xl font-bold text-slate-900" data-id="element-19559">
                {sub.price} ₽
                <span className="text-sm font-normal text-slate-400" data-id="element-19560">
                  {' '}
                  /{t('userdash.subscriptions.month')}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-6" data-id="element-19561">
            <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2" data-id="element-19562">
              <Clock className="w-4 h-4 text-slate-400" data-id="element-19563" />
              <div data-id="element-19564">
                <div className="text-[10px] text-slate-500" data-id="element-19565">
                  {t('userdash.subscriptions.nextBilling')}
                </div>
                <div className="text-xs font-bold text-slate-900" data-id="element-19566">
                  {sub.nextBilling}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2" data-id="element-19567">
              <Calendar className="w-4 h-4 text-slate-400" data-id="element-19568" />
              <div data-id="element-19569">
                <div className="text-[10px] text-slate-500" data-id="element-19570">
                  {t('userdash.subscriptions.timeLeft')}
                </div>
                <div className="text-xs font-bold text-slate-900" data-id="element-19571">
                  {sub.daysLeft} {t('userdash.subscriptions.days')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Usage Stats — "Вы уже получили" */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-19572">
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2" data-id="element-19573">
          <Sparkles className="w-4 h-4 text-indigo-600" data-id="element-19574" />
          <span className="text-sm font-bold text-slate-900" data-id="element-19575">
            {t('userdash.subscriptions.youAlreadyGot')}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-slate-100" data-id="element-19576">
          <div className="p-4 text-center" data-id="element-19577">
            <div className="text-2xl font-bold text-indigo-600" data-id="element-19578">
              {sub.reportsUsed}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-19579">
              {t('userdash.subscriptions.reportsUsed')}
            </div>
          </div>
          <div className="p-4 text-center" data-id="element-19580">
            <div className="text-2xl font-bold text-emerald-600" data-id="element-19581">
              {sub.reportsRemaining}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-19582">
              {t('userdash.subscriptions.reportsRemaining')}
            </div>
          </div>
          <div className="p-4 text-center" data-id="element-19583">
            <div className="text-2xl font-bold text-amber-600" data-id="element-19584">
              {sub.savingsAmount.toLocaleString('ru-RU')} ₽
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-19585">
              {t('userdash.subscriptions.savedOnReports')}
            </div>
          </div>
          <div className="p-4 text-center" data-id="element-19586">
            <div className="text-2xl font-bold text-purple-600" data-id="element-19587">
              {sub.discountSaved.toLocaleString('ru-RU')} ₽
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-19588">
              {t('userdash.subscriptions.savedOnDiscounts')}
            </div>
          </div>
        </div>
      </div>

      {/* Reports remaining bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-id="element-19589">
        <div className="flex items-center justify-between mb-2" data-id="element-19590">
          <span className="text-xs font-bold text-slate-900" data-id="element-19591">
            {t('userdash.subscriptions.freeReportsThisMonth')}
          </span>
          <span className="text-xs text-slate-500" data-id="element-19592">
            {t('userdash.subscriptions.usedOfTotal', {
            used: sub.reportsUsed.toString(),
            total: sub.reportsTotal.toString()
          })}
          </span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-2.5" data-id="element-19593">
          <div className="bg-indigo-500 h-2.5 rounded-full transition-all" style={{
          width: `${sub.reportsUsed / sub.reportsTotal * 100}%`
        }} data-id="element-19594" />
        </div>
        <p className="text-[10px] text-slate-500 mt-1.5" data-id="element-19595">
          {t('userdash.subscriptions.left')}{' '}
          <span className="font-bold text-indigo-600" data-id="element-19596">
            {sub.reportsRemaining}
          </span>{' '}
          {t('userdash.subscriptions.freeReportsUpdate', {
          date: '12 Ноя 2024'
        })}
        </p>
      </div>

      {/* Benefits */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-19597">
        {benefits.map((b, i) => <div key={i} className="bg-white border border-slate-200 rounded-xl p-3 text-center shadow-sm" data-id="element-19598">
            <div className={`w-9 h-9 rounded-lg ${b.bg} flex items-center justify-center mx-auto mb-2 ${b.text}`} data-id="element-19599">
              {b.icon}
            </div>
            <div className="text-xs font-bold text-slate-900" data-id="element-19600">{b.label}</div>
            <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-19601">{b.desc}</div>
          </div>)}
      </div>

      {/* Actions */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3" data-id="element-19602">
        <div className="flex items-center justify-between" data-id="element-19603">
          <h4 className="text-sm font-bold text-slate-900" data-id="element-19604">
            {t('userdash.subscriptions.management')}
          </h4>
          <Badge variant="neutral" size="sm" className="text-[9px]" data-id="element-19605">
            {t('userdash.subscriptions.comingSoon')}
          </Badge>
        </div>
        <p className="text-xs text-slate-500" data-id="element-19606">
          {t('userdash.subscriptions.managementDesc')}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-19607">
          <Button variant="primary" size="sm" className="text-xs justify-center opacity-50 cursor-not-allowed" disabled data-id="element-19608">
            <ArrowRight className="w-3.5 h-3.5 mr-1" data-id="element-19609" />{' '}
            {t('userdash.subscriptions.changePlan')}
          </Button>
          <Button variant="outline" size="sm" className="text-xs justify-center opacity-50 cursor-not-allowed" disabled data-id="element-19610">
            <RefreshCw className="w-3.5 h-3.5 mr-1" data-id="element-19611" />{' '}
            {t('userdash.subscriptions.renewEarly')}
          </Button>
          <Button variant="outline" size="sm" className="text-xs justify-center opacity-50 cursor-not-allowed" disabled data-id="element-19612">
            <Pause className="w-3.5 h-3.5 mr-1" data-id="element-19613" />{' '}
            {t('userdash.subscriptions.pause')}
          </Button>
          <Button variant="outline" size="sm" className="text-xs justify-center opacity-50 cursor-not-allowed" disabled data-id="element-19614">
            <X className="w-3.5 h-3.5 mr-1" data-id="element-19615" />{' '}
            {t('userdash.subscriptions.cancelSubscription')}
          </Button>
        </div>
      </div>

      {/* Cancel Warning */}
      {showCancelWarning && <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 space-y-3" data-id="element-19616">
          <div className="flex items-start gap-3" data-id="element-19617">
            <AlertTriangle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" data-id="element-19618" />
            <div data-id="element-19619">
              <h4 className="font-bold text-rose-900 text-sm" data-id="element-19620">
                {t('userdash.subscriptions.areYouSure')}
              </h4>
              <p className="text-xs text-rose-700 mt-1 leading-relaxed" data-id="element-19621">
                {t('userdash.subscriptions.cancelWarning')}
              </p>
              <ul className="mt-2 space-y-1" data-id="element-19622">
                <li className="flex items-center gap-1.5 text-xs text-rose-700" data-id="element-19623">
                  <X className="w-3 h-3 shrink-0" data-id="element-19624" /> {sub.reportsRemaining}{' '}
                  {t('userdash.subscriptions.unusedReports')}
                </li>
                <li className="flex items-center gap-1.5 text-xs text-rose-700" data-id="element-19625">
                  <X className="w-3 h-3 shrink-0" data-id="element-19626" />{' '}
                  {t('userdash.subscriptions.discountLost')}
                </li>
                <li className="flex items-center gap-1.5 text-xs text-rose-700" data-id="element-19627">
                  <X className="w-3 h-3 shrink-0" data-id="element-19628" />{' '}
                  {t('userdash.subscriptions.proFeaturesLost')}
                </li>
              </ul>
              <p className="text-[10px] text-rose-600 mt-2" data-id="element-19629">
                {t('userdash.subscriptions.youSavedTotal')}{' '}
                <span className="font-bold" data-id="element-19630">
                  {(sub.savingsAmount + sub.discountSaved).toLocaleString('ru-RU')}{' '}
                  ₽
                </span>
              </p>
            </div>
          </div>
          <div className="flex gap-2 pt-1" data-id="element-19631">
            <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => setShowCancelWarning(false)} data-id="element-19632">
              {t('userdash.subscriptions.keepSubscription')}
            </Button>
            <Button variant="outline" size="sm" className="flex-1 text-xs text-rose-600 border-rose-300 hover:bg-rose-100" onClick={() => {
          setHasSubscription(false);
          setShowCancelWarning(false);
          showToast(t('userdash.subscriptions.cancelledNotice'));
        }} data-id="element-19633">
              {t('userdash.subscriptions.cancelAnyway')}
            </Button>
          </div>
        </div>}

      {/* Savings highlight */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center gap-3" data-id="element-19634">
        <Shield className="w-5 h-5 text-emerald-600 shrink-0" data-id="element-19635" />
        <p className="text-xs text-emerald-800" data-id="element-19636">
          {t('userdash.subscriptions.totalSavedHighlight')}{' '}
          <span className="font-bold" data-id="element-19637">
            {(sub.savingsAmount + sub.discountSaved).toLocaleString('ru-RU')} ₽
          </span>
          . {t('userdash.subscriptions.averageSavings')}
        </p>
      </div>
    </div>;
}